import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { X } from 'lucide-react';
import type { LeadData } from '@/hooks/useEventChatbot';

interface LeadCaptureFormProps {
  onSubmit: (data: LeadData) => void;
  onClose: () => void;
  eventName?: string;
}

export function LeadCaptureForm({ onSubmit, onClose, eventName }: LeadCaptureFormProps) {
  const [formData, setFormData] = useState<LeadData>({
    name: '',
    email: '',
    phone: '',
    interest_type: eventName || '',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-card border rounded-lg p-4 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-lg">Stay Connected 🎉</h3>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        Share your details and our team will reach out with all the info you need!
      </p>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <Label htmlFor="lead-name" className="text-sm">
            Name *
          </Label>
          <Input
            id="lead-name"
            placeholder="Your name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            required
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="lead-email" className="text-sm">
            Email *
          </Label>
          <Input
            id="lead-email"
            type="email"
            placeholder="your@email.com"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            required
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="lead-phone" className="text-sm">
            Phone (optional)
          </Label>
          <Input
            id="lead-phone"
            type="tel"
            placeholder="+41 ..."
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="lead-notes" className="text-sm">
            Anything else you'd like us to know?
          </Label>
          <Textarea
            id="lead-notes"
            placeholder="E.g., preferred dates, group size, questions..."
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            className="mt-1 min-h-[60px]"
          />
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={isSubmitting || !formData.name || !formData.email}
        >
          {isSubmitting ? 'Sending...' : 'Send My Details'}
        </Button>
      </form>
    </div>
  );
}
